import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import EmulatorJS from './EmulatorJS';
import Ruffle from './Ruffle';
import Apple2TS from './Apple2TS';
import HTML from './HTML';
import JSDos from './JSDos';
import Emularity from './Emularity';
import Encrusted from './Encrusted';
import FreeJ2ME from './FreeJ2ME';
import MobileControls from './MobileControls';
import './EmulatorContainer.css';

const EmulatorContainer = ({ game, platform, selectedRomVersion, romVersions, onFullscreen, pageContext = 'game' }) => {
  const [rufflePlayer, setRufflePlayer] = useState(null);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showFullscreenNotice, setShowFullscreenNotice] = useState(false);
  const containerRef = useRef(null);
  
  // Determine which emulator to use: game's custom emulator takes precedence over platform default
  const activeEmulator = game.emulator || platform.emulator;

  // Detect if user is on iOS
  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  // Check if fullscreen API is supported
  const isFullscreenSupported = () => {
    return !!(document.fullscreenEnabled || 
              document.webkitFullscreenEnabled || 
              document.mozFullScreenEnabled || 
              document.msFullscreenEnabled);
  };

  // Check fullscreen support on component mount for EJS
  React.useEffect(() => {
    if (activeEmulator === 'ejs') {
      if (!isFullscreenSupported()) {
        setShowFullscreenNotice(true);
      }
    }
  }, [activeEmulator]);

  const handleFullscreen = async () => {
    if (onFullscreen) {
      onFullscreen();
      return;
    }

    // Check if fullscreen API is supported
    const supportsFullscreen = document.fullscreenEnabled || 
                              document.webkitFullscreenEnabled || 
                              document.msFullscreenEnabled;

    // If on iOS and fullscreen API is not supported, show instructions
    if (isIOS() && !supportsFullscreen) {
      setShowIOSInstructions(true);
      return;
    }

    let fullscreenPromise = null;

    // Fallback fullscreen handling
    if (activeEmulator === 'ruffle' && rufflePlayer) {
      rufflePlayer.enterFullscreen();
      return;
    } else if (activeEmulator === 'apple2ts') {
      const apple2tsFrame = containerRef.current?.querySelector('.apple2ts-frame');
      if (apple2tsFrame) {
        if (apple2tsFrame.requestFullscreen) {
          fullscreenPromise = apple2tsFrame.requestFullscreen();
        } else if (apple2tsFrame.webkitRequestFullscreen) { /* Safari */
          fullscreenPromise = apple2tsFrame.webkitRequestFullscreen();
        } else if (apple2tsFrame.msRequestFullscreen) { /* IE11 */
          fullscreenPromise = apple2tsFrame.msRequestFullscreen();
        }
      }
    } else if (activeEmulator === 'html') {
      const htmlFrame = containerRef.current?.querySelector('.html-frame');
      if (htmlFrame) {
        if (htmlFrame.requestFullscreen) {
          fullscreenPromise = htmlFrame.requestFullscreen();
        } else if (htmlFrame.webkitRequestFullscreen) { /* Safari */
          fullscreenPromise = htmlFrame.webkitRequestFullscreen();
        } else if (htmlFrame.msRequestFullscreen) { /* IE11 */
          fullscreenPromise = htmlFrame.msRequestFullscreen();
        }
      }
    } else if (activeEmulator === 'jsdos') {
      const jsdosContainer = containerRef.current?.querySelector('.js-dos-container');
      if (jsdosContainer) {
        if (jsdosContainer.requestFullscreen) {
          fullscreenPromise = jsdosContainer.requestFullscreen();
        } else if (jsdosContainer.webkitRequestFullscreen) { /* Safari */
          fullscreenPromise = jsdosContainer.webkitRequestFullscreen();
        } else if (jsdosContainer.msRequestFullscreen) { /* IE11 */
          fullscreenPromise = jsdosContainer.msRequestFullscreen();
        }
      }
    } else if (activeEmulator === 'emularity') {
      const emularityFrame = containerRef.current?.querySelector('.emularity-frame');
      if (emularityFrame) {
        if (emularityFrame.requestFullscreen) {
          fullscreenPromise = emularityFrame.requestFullscreen();
        } else if (emularityFrame.webkitRequestFullscreen) { /* Safari */
          fullscreenPromise = emularityFrame.webkitRequestFullscreen();
        } else if (emularityFrame.msRequestFullscreen) { /* IE11 */
          fullscreenPromise = emularityFrame.msRequestFullscreen();
        }
      }
    } else if (activeEmulator === 'encrusted') {
      const encrustedFrame = containerRef.current?.querySelector('.encrusted-frame');
      if (encrustedFrame) {
        if (encrustedFrame.requestFullscreen) {
          fullscreenPromise = encrustedFrame.requestFullscreen();
        } else if (encrustedFrame.webkitRequestFullscreen) { /* Safari */
          fullscreenPromise = encrustedFrame.webkitRequestFullscreen();
        } else if (encrustedFrame.msRequestFullscreen) { /* IE11 */
          fullscreenPromise = encrustedFrame.msRequestFullscreen();
        }
      }
    } else if (activeEmulator === 'freej2me') {
      const freej2meFrame = containerRef.current?.querySelector('.freej2me-iframe');
      if (freej2meFrame) {
        if (freej2meFrame.requestFullscreen) {
          fullscreenPromise = freej2meFrame.requestFullscreen();
        } else if (freej2meFrame.webkitRequestFullscreen) {
          fullscreenPromise = freej2meFrame.webkitRequestFullscreen();
        } else if (freej2meFrame.msRequestFullscreen) {
          fullscreenPromise = freej2meFrame.msRequestFullscreen();
        }
      }
    }

    // Handle fullscreen promise rejection (common on iOS)
    if (fullscreenPromise && typeof fullscreenPromise.catch === 'function') {
      fullscreenPromise.catch((error) => {
        console.log('Fullscreen request failed:', error);
        if (isIOS()) {
          setShowIOSInstructions(true);
        }
      });
    }
  };

  const renderEmulator = () => {
    if (!platform) return <div>Loading emulator...</div>;

    const emulator = activeEmulator;

    switch (emulator) {
      case 'ejs':
        return (
          <>
            <EmulatorJS 
              game={game} 
              platform={platform} 
              selectedRomVersion={selectedRomVersion} 
            />
            {showFullscreenNotice && (
              <div className={`fullscreen-notice-bar fullscreen-notice-${pageContext}`}>
                <span>全屏功能未启用</span>
                <button 
                  className="notice-details-btn"
                  onClick={() => setShowIOSInstructions(true)}
                >
                  查看详情
                </button>
                <button 
                  className="notice-close-btn"
                  onClick={() => setShowFullscreenNotice(false)}
                >
                  ×
                </button>
              </div>
            )}
          </>
        );
      
      case 'ruffle':
        return <Ruffle 
          game={game} 
          onPlayerRef={setRufflePlayer} 
        />;
      
      case 'apple2ts':
        return <Apple2TS 
          game={game} 
        />;
      
      case 'html':
        return <HTML
          game={game}
          selectedRomVersion={selectedRomVersion}
        />;
      
      case 'jsdos':
        return <JSDos
          game={game}
          selectedRomVersion={selectedRomVersion}
        />;
      
      case 'emularity':
        return <Emularity
          game={game}
          selectedRomVersion={selectedRomVersion}
        />;
        
      case 'encrusted':
        return <Encrusted
          game={game}
          selectedRomVersion={selectedRomVersion}
        />;

      case 'freej2me':
        return <FreeJ2ME
          game={game}
          selectedRomVersion={selectedRomVersion}
        />;
      
      default:
        return <div>Unsupported emulator type: {emulator}</div>;
    }
  };

  // Only show fullscreen button for certain emulators
  const showFullscreenButton = ['ruffle', 'apple2ts', 'html', 'emularity', 'encrusted', 'freej2me'].includes(activeEmulator);
  
  // Show mobile controls only for Flash games
  const showMobileControls = activeEmulator === 'ruffle';

  return (
    <div className="emulator-wrapper">
      <div className="emulator-container" ref={containerRef}>
        {renderEmulator()}
        {showFullscreenButton && (
          <button className="emulator-fullscreen-button" onClick={handleFullscreen}>
            <span className="fullscreen-icon">⛶</span>
          </button>
        )}
      </div>
      {showMobileControls && <MobileControls rufflePlayer={rufflePlayer} />}
      
      {/* iOS Fullscreen Instructions Modal */}
      {showIOSInstructions && (
        <div className="ios-instructions-overlay" onClick={() => setShowIOSInstructions(false)}>
          <div className="ios-instructions-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ios-instructions-header">
              <h3>启用全屏功能</h3>
              <button 
                className="ios-instructions-close" 
                onClick={() => setShowIOSInstructions(false)}
              >
                ×
              </button>
            </div>
            <div className="ios-instructions-content">
              <p>全屏功能仅在Safari浏览器中可用。其他浏览器在iOS上不支持此功能。</p>
              
              <div className="ios-instruction-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <strong>设置 → Apps → Safari浏览器 → 高级 → 功能开关</strong>
                  <p>启用"全屏API"功能</p>
                </div>
              </div>
              
              <div className="ios-instruction-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <strong>重启Safari浏览器</strong>
                  <p>清除后台任务并重新启动Safari，然后重试全屏功能</p>
                </div>
              </div>
            </div>
            <div className="ios-instructions-footer">
              <button 
                className="ios-instructions-button" 
                onClick={() => setShowIOSInstructions(false)}
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

EmulatorContainer.propTypes = {
  game: PropTypes.object.isRequired,
  platform: PropTypes.object.isRequired,
  selectedRomVersion: PropTypes.string.isRequired,
  romVersions: PropTypes.array.isRequired,
  onFullscreen: PropTypes.func,
  pageContext: PropTypes.oneOf(['feed', 'game'])
};

export default EmulatorContainer;